import React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Plus,
  Search,
  Download,
  Upload,
  AlertCircle,
  CheckCircle2,
  ChevronRight,
} from '../icons';
import { Button, cx } from '../ui';
import {
  ALL_PGA_CODES,
  PGA_FLAG_SCHEMAS,
  pgaBadgeTone,
  type PgaCode,
} from '../lib/pga-schema';
import type { ParsedTemplate } from '../lib/pga-template';
import {
  downloadImportPgaFlagTemplate,
  fetchImportPgaFlagDefinitions,
  fetchImportPgaFlagDefinition,
  type ImportPgaFlagDefinition,
  previewImportPgaFlagTemplate,
  type ImportPgaFlagDefinitionSummary,
} from '../lib/import-service';
import {
  buildPgaHitPrefill,
  createLinkedPgaHitSnapshot,
  PGA_LINKED_HIT_STORAGE_KEY,
  type PgaHitPrefillField,
  type PgaHitOption,
} from '../lib/pga-hit-prefill';

const TONE_CLS: Record<'info' | 'warning' | 'danger', string> = {
  info: 'ih-badge-info',
  warning: 'ih-badge-warning',
  danger: 'ih-badge-danger',
};

export type { PgaHitOption } from '../lib/pga-hit-prefill';

export const PgaFlagCreateDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  appliedCodes: PgaCode[];
  hits: PgaHitOption[];
  initialHit?: PgaHitOption | null;
  productDefaults?: Record<string, unknown>;
  onCreate: (args: {
    code: PgaCode;
    seedValues: Record<string, unknown>;
    seedRepeaters: Record<string, Record<string, unknown>[]>;
    linkedHit?: PgaHitOption;
  }) => void;
}> = ({
  open,
  onClose,
  appliedCodes,
  hits,
  initialHit,
  productDefaults,
  onCreate,
}) => {
  // 1. All hooks (useState, useRef, useMemo) MUST go first
  const [code, setCode] = useState<PgaCode | null>(null);
  const [typeSearch, setTypeSearch] = useState('');
  const [hitSearch, setHitSearch] = useState('');
  const [linkedHitId, setLinkedHitId] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedTemplate | null>(null);
  const [parsedFileName, setParsedFileName] = useState<string | null>(null);
  const [definitions, setDefinitions] = useState<
    ImportPgaFlagDefinitionSummary[]
  >([]);
  const [definition, setDefinition] = useState<ImportPgaFlagDefinition | null>(
    null,
  );
  const [definitionBusy, setDefinitionBusy] = useState(false);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchImportPgaFlagDefinitions().then((result) => {
      if (cancelled) return;
      if (result.success) setDefinitions(result.definitions);
      else setTemplateError(result.error ?? 'Failed to load PGA templates.');
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setCode(initialHit?.code ?? null);
    setLinkedHitId(initialHit?.id ?? null);
    setHitSearch('');
    setParsed(null);
    setParsedFileName(null);
  }, [open, initialHit?.code, initialHit?.id]);

  useEffect(() => {
    if (!open || !code) {
      setDefinition(null);
      setDefinitionBusy(false);
      return;
    }
    let cancelled = false;
    setDefinition(null);
    setDefinitionBusy(true);
    setTemplateError(null);
    fetchImportPgaFlagDefinition(code)
      .then((result) => {
        if (cancelled) return;
        if (result.success && result.definition) {
          setDefinition(result.definition);
        } else {
          setTemplateError(result.error ?? 'Failed to load PGA fields.');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTemplateError('Failed to load PGA fields.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDefinitionBusy(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [code, open]);

  const hitCounts = useMemo(() => {
    const counts = new Map<PgaCode, number>();
    hits.forEach((hit) =>
      counts.set(hit.code, (counts.get(hit.code) ?? 0) + 1),
    );
    return counts;
  }, [hits]);

  const filteredTypes = useMemo(() => {
    const needle = typeSearch.trim().toLowerCase();
    return [...ALL_PGA_CODES]
      .filter((c) => {
        if (!needle) return true;
        const s = PGA_FLAG_SCHEMAS[c];
        return (
          c.toLowerCase().includes(needle) ||
          s.agency.toLowerCase().includes(needle) ||
          s.displayName.toLowerCase().includes(needle)
        );
      })
      .sort((a, b) => (hitCounts.get(b) ?? 0) - (hitCounts.get(a) ?? 0));
  }, [hitCounts, typeSearch]);

  const typeHits = useMemo(() => {
    if (!code) return [];
    const needle = hitSearch.trim().toLowerCase();
    return hits
      .filter((h) => h.code === code)
      .filter((h) =>
        !needle
          ? true
          : [h.listName, h.agency, ...Object.values(h.raw).map(String)].some(
              (v) => v.toLowerCase().includes(needle),
            ),
      );
  }, [code, hits, hitSearch]);

  const linkedHit = useMemo(
    () => hits.find((hit) => hit.id === linkedHitId) ?? null,
    [hits, linkedHitId],
  );
  const hitPrefill = useMemo(
    () => (linkedHit ? buildPgaHitPrefill(linkedHit, definition) : null),
    [definition, linkedHit],
  );

  // 2. Early returns happen AFTER all hooks are declared
  if (!open) return null;
  if (typeof document === 'undefined') return null;

  // 3. The rest of the component logic
  const reset = () => {
    setCode(null);
    setTypeSearch('');
    setHitSearch('');
    setLinkedHitId(null);
    setParsed(null);
    setParsedFileName(null);
    setDefinition(null);
    setDefinitionBusy(false);
    setTemplateBusy(false);
    setTemplateError(null);
  };
  const close = () => {
    reset();
    onClose();
  };

  const handleFile = async (file: File) => {
    if (!code) return;
    setTemplateBusy(true);
    setTemplateError(null);
    try {
      const text = await file.text();
      const result = await previewImportPgaFlagTemplate(code, text);
      if (!result.success || !result.preview) {
        setParsed(null);
        setTemplateError(result.error ?? 'Failed to read the PGA template.');
        return;
      }
      setParsed(result.preview);
      setParsedFileName(file.name);
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleDownload = async () => {
    if (!code) return;
    setTemplateBusy(true);
    setTemplateError(null);
    const result = await downloadImportPgaFlagTemplate(code);
    if (!result.success) {
      setTemplateError(result.error ?? 'Failed to download the PGA template.');
    }
    setTemplateBusy(false);
  };

  const handleCreate = () => {
    if (!code) return;
    // Product-owned identity wins over the regulatory match's scope data.
    // A completed template remains the user's most explicit input and wins
    // over both sources below.
    const seedValues: Record<string, unknown> = {
      ...(hitPrefill?.values ?? {}),
    };
    Object.entries(productDefaults ?? {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        seedValues[key] = value;
      }
    });
    const seedRepeaters: Record<string, Record<string, unknown>[]> = {
      ...(hitPrefill?.repeaters ?? {}),
    };
    if (linkedHit) {
      seedValues[PGA_LINKED_HIT_STORAGE_KEY] =
        createLinkedPgaHitSnapshot(linkedHit);
    }
    if (parsed) {
      Object.assign(seedValues, parsed.values);
      Object.assign(seedRepeaters, parsed.repeaters);
    }
    onCreate({
      code,
      seedValues,
      seedRepeaters,
      linkedHit: linkedHit ?? undefined,
    });
    reset();
  };

  const schema = code ? PGA_FLAG_SCHEMAS[code] : null;
  const templateFieldCount = code
    ? definitions.find((definition) => definition.code === code)?.fieldCount ??
      0
    : 0;
  const hasHitsForType = code ? hits.some((h) => h.code === code) : false;

  return createPortal(
    <div className='ih-modal-backdrop' role='dialog' aria-modal='true'>
      <div
        className='ih-dialog ih-dialog-lg'
        style={{ maxHeight: 'calc(100vh - 32px)' }}
      >
        <div className='ih-dialog-header'>
          <div>
            <div className='ih-kicker'>New program code flag</div>
            <div className='ih-dialog-title'>Create PGA flag manually</div>
          </div>
          <button
            type='button'
            onClick={close}
            className='ih-icon-btn'
            aria-label='Close'
          >
            <X size={16} />
          </button>
        </div>

        <div
          className='ih-dialog-body ih-col ih-gap-4'
          style={{ overflowY: 'auto' }}
        >
          {/* Step 1 — pick PGA type */}
          <section className='ih-col ih-gap-2'>
            <StepHeader n={1} title='Select PGA flag type' required />
            <div className='ih-input-group'>
              <span className='ih-input-group-icon'>
                <Search size={14} />
              </span>
              <input
                value={typeSearch}
                onChange={(e) => setTypeSearch(e.target.value)}
                placeholder='Search FDA, EPA, Lacey…'
                className='ih-input ih-input-sm ih-input-search'
              />
            </div>
            <div className='ih-pga-type-grid'>
              {filteredTypes.map((c) => {
                const s = PGA_FLAG_SCHEMAS[c];
                const isApplied = appliedCodes.includes(c);
                const active = code === c;
                const hitCount = hitCounts.get(c) ?? 0;
                return (
                  <button
                    key={c}
                    type='button'
                    disabled={isApplied}
                    onClick={() => {
                      setCode(c);
                      setLinkedHitId(null);
                      setParsed(null);
                      setParsedFileName(null);
                      setHitSearch('');
                    }}
                    className={cx(
                      'ih-pga-type-tile',
                      active && 'is-active',
                      isApplied && 'is-disabled',
                    )}
                    title={
                      isApplied
                        ? 'A flag of this type already exists on this product.'
                        : s.displayName
                    }
                  >
                    <div
                      className='ih-row ih-between ih-gap-2'
                      style={{ width: '100%' }}
                    >
                      <span
                        className={cx('ih-badge', TONE_CLS[pgaBadgeTone(c)])}
                      >
                        {c.replace('_', ' ')}
                      </span>
                      {isApplied ? (
                        <span className='ih-text-xs ih-tone-success ih-row ih-gap-1'>
                          <CheckCircle2 size={11} /> Applied
                        </span>
                      ) : hitCount > 0 ? (
                        <span className='ih-badge ih-badge-info'>
                          {hitCount} hit{hitCount === 1 ? '' : 's'}
                        </span>
                      ) : active ? (
                        <CheckCircle2 size={13} className='ih-tone-info' />
                      ) : null}
                    </div>
                    <div className='ih-pga-type-tile-agency ih-truncate'>
                      {s.agency}
                    </div>
                    <div className='ih-pga-type-tile-name ih-truncate'>
                      {s.displayName}
                    </div>
                  </button>
                );
              })}
              {filteredTypes.length === 0 && (
                <div
                  className='ih-subsurface ih-text-xs ih-text-muted ih-italic ih-text-center'
                  style={{ padding: 12, gridColumn: '1 / -1' }}
                >
                  No matching PGA types.
                </div>
              )}
            </div>
          </section>

          {/* Step 2 — optional screening-hit prefill */}
          {code && (
            <section className='ih-col ih-gap-2'>
              <StepHeader
                n={2}
                title='Use a screening hit to prefill'
                optional
                hint={
                  hasHitsForType
                    ? 'Choose a relevant hit to prefill known program fields.'
                    : undefined
                }
              />
              {!hasHitsForType ? (
                <div
                  className='ih-subsurface ih-text-xs ih-text-muted ih-italic'
                  style={{ padding: '10px 12px' }}
                >
                  No {code.replace('_', ' ')} hits available for this product.
                  You can still create the flag standalone.
                </div>
              ) : (
                <>
                  <div className='ih-input-group'>
                    <span className='ih-input-group-icon'>
                      <Search size={14} />
                    </span>
                    <input
                      value={hitSearch}
                      onChange={(e) => setHitSearch(e.target.value)}
                      placeholder='Search hits…'
                      className='ih-input ih-input-sm ih-input-search'
                    />
                  </div>
                  <div className='ih-pga-hit-list'>
                    <button
                      type='button'
                      onClick={() => setLinkedHitId(null)}
                      className={cx(
                        'ih-pga-hit-item',
                        !linkedHit && 'is-active',
                      )}
                    >
                      <div className='ih-col' style={{ minWidth: 0 }}>
                        <div className='ih-font-medium ih-text-xs'>
                          No hit — standalone flag
                        </div>
                        <div className='ih-text-xs ih-text-muted'>
                          Create without pre-filling from a screening hit.
                        </div>
                      </div>
                      {!linkedHit && (
                        <CheckCircle2 size={13} className='ih-tone-info' />
                      )}
                    </button>
                    {typeHits.map((h, i) => {
                      const isActive = linkedHitId === h.id;
                      return (
                        <button
                          key={h.id || i}
                          type='button'
                          onClick={() => setLinkedHitId(h.id)}
                          className={cx(
                            'ih-pga-hit-item',
                            isActive && 'is-active',
                          )}
                        >
                          <div className='ih-col' style={{ minWidth: 0 }}>
                            <div className='ih-font-medium ih-text-xs ih-truncate'>
                              {h.listName}
                            </div>
                            <div className='ih-text-xs ih-text-muted ih-truncate'>
                              {h.agency} ·{' '}
                              {String(
                                h.raw['Program'] ?? h.raw['HS Code'] ?? '—',
                              )}
                            </div>
                          </div>
                          {isActive && (
                            <CheckCircle2 size={13} className='ih-tone-info' />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {linkedHit && (
                    <HitPrefillPreview
                      hit={linkedHit}
                      busy={definitionBusy}
                      fields={hitPrefill?.fields ?? []}
                    />
                  )}
                </>
              )}
            </section>
          )}

          {/* Step 3 — optional template */}
          {code && (
            <section className='ih-col ih-gap-2'>
              <StepHeader
                n={3}
                title='Populate via template'
                optional
                hint='Download → fill offline → upload → review.'
              />
              <div className='ih-row ih-gap-2 ih-row-wrap'>
                <Button
                  variant='outline'
                  size='sm'
                  className='ih-pga-modal-secondary-action'
                  onClick={handleDownload}
                  disabled={templateBusy}
                >
                  <Download size={14} /> Download {code.replace('_', ' ')}{' '}
                  template
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  className='ih-pga-modal-secondary-action'
                  onClick={() => fileRef.current?.click()}
                  disabled={templateBusy}
                >
                  <Upload size={14} /> Upload completed template
                </Button>
                <span className='ih-text-xs ih-text-muted'>
                  {templateFieldCount || '—'} workbook fields · CSV
                </span>
                <input
                  ref={fileRef}
                  type='file'
                  accept='.csv,text/csv'
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFile(f);
                    e.target.value = '';
                  }}
                />
              </div>
              {templateError && (
                <div className='ih-text-xs ih-tone-danger'>{templateError}</div>
              )}
              {parsed && (
                <ReviewParsed
                  parsed={parsed}
                  fileName={parsedFileName}
                  onClear={() => {
                    setParsed(null);
                    setParsedFileName(null);
                  }}
                />
              )}
            </section>
          )}
        </div>

        <div className='ih-dialog-footer'>
          <Button
            variant='outline'
            size='sm'
            className='ih-pga-modal-secondary-action'
            onClick={close}
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            size='sm'
            onClick={handleCreate}
            disabled={
              !code ||
              (!!linkedHit && definitionBusy) ||
              !!parsed?.errors.length ||
              !!parsed?.duplicateHeaders.length
            }
          >
            <Plus size={14} /> Create flag & open editor{' '}
            <ChevronRight size={14} />
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const StepHeader: React.FC<{
  n: number;
  title: string;
  required?: boolean;
  optional?: boolean;
  hint?: string;
}> = ({ n, title, required, optional, hint }) => {
  return (
    <div className='ih-row ih-between ih-gap-2'>
      <div className='ih-row ih-gap-2'>
        <span className='ih-step-num'>{n}</span>
        <span className='ih-font-semibold ih-text-sm'>{title}</span>
        {required && <span className='ih-tone-warning ih-text-xs'>*</span>}
        {optional && (
          <span className='ih-text-xs ih-text-muted'>(optional)</span>
        )}
      </div>
      {hint && (
        <span className='ih-text-xs ih-text-muted ih-truncate'>{hint}</span>
      )}
    </div>
  );
};

const HitPrefillPreview: React.FC<{
  hit: PgaHitOption;
  busy: boolean;
  fields: PgaHitPrefillField[];
}> = ({ hit, busy, fields }) => (
  <div className='ih-subsurface ih-col ih-gap-2' style={{ padding: 12 }}>
    <div className='ih-row ih-between ih-gap-2'>
      <div style={{ minWidth: 0 }}>
        <div className='ih-kicker'>Attached screening source</div>
        <div className='ih-font-medium ih-text-xs ih-truncate'>
          {hit.listName}
        </div>
      </div>
      <span className='ih-badge ih-badge-info' style={{ flexShrink: 0 }}>
        {busy
          ? 'Checking fields…'
          : `${fields.length} prefill${fields.length === 1 ? '' : 's'}`}
      </span>
    </div>
    {!busy && fields.length === 0 && (
      <div className='ih-text-xs ih-text-muted'>
        This hit will be attached for traceability. It does not contain filing
        fields that can be safely prefilled.
      </div>
    )}
    {!busy && fields.length > 0 && (
      <div className='ih-pga-review-grid'>
        {fields.slice(0, 8).map((field) => (
          <div key={field.key} className='ih-pga-review-row'>
            <span className='ih-text-xs ih-text-muted ih-truncate'>
              {field.label}
            </span>
            <span className='ih-text-xs ih-font-medium ih-truncate'>
              {String(field.value)}
            </span>
          </div>
        ))}
        {fields.length > 8 && (
          <div className='ih-text-xs ih-text-muted ih-italic'>
            + {fields.length - 8} more…
          </div>
        )}
      </div>
    )}
  </div>
);

const ReviewParsed: React.FC<{
  parsed: ParsedTemplate;
  fileName: string | null;
  onClear: () => void;
}> = ({ parsed, fileName, onClear }) => {
  const entries = Object.entries(parsed.values);
  const hasIssues =
    parsed.errors.length > 0 ||
    parsed.missingRequired.length > 0 ||
    parsed.unmatchedHeaders.length > 0 ||
    parsed.duplicateHeaders.length > 0;
  return (
    <div className='ih-subsurface ih-col ih-gap-2' style={{ padding: 12 }}>
      <div className='ih-row ih-between ih-gap-2'>
        <div className='ih-row ih-gap-2'>
          {hasIssues ? (
            <AlertCircle size={14} className='ih-tone-warning' />
          ) : (
            <CheckCircle2 size={14} className='ih-tone-success' />
          )}
          <div className='ih-font-medium ih-text-xs'>
            {parsed.matchedColumns} field
            {parsed.matchedColumns === 1 ? '' : 's'} parsed from{' '}
            {fileName ?? 'template'}
          </div>
        </div>
        <button
          type='button'
          onClick={onClear}
          className='ih-btn-link ih-text-xs'
        >
          Clear
        </button>
      </div>
      {parsed.errors.length > 0 && (
        <ul
          className='ih-col ih-gap-1 ih-text-xs ih-tone-danger'
          style={{ paddingLeft: 16, listStyle: 'disc' }}
        >
          {parsed.errors.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      )}
      {parsed.missingRequired.length > 0 && (
        <div className='ih-text-xs ih-tone-warning'>
          Missing required: {parsed.missingRequired.join(', ')}
        </div>
      )}
      {parsed.unmatchedHeaders.length > 0 && (
        <div className='ih-text-xs ih-text-muted'>
          Ignored headers: {parsed.unmatchedHeaders.join(', ')}
        </div>
      )}
      {parsed.duplicateHeaders.length > 0 && (
        <div className='ih-text-xs ih-tone-warning'>
          Duplicate headers: {parsed.duplicateHeaders.join(', ')}
        </div>
      )}
      {entries.length > 0 && (
        <div className='ih-pga-review-grid'>
          {entries.slice(0, 10).map(([k, v]) => (
            <div key={k} className='ih-pga-review-row'>
              <span className='ih-text-xs ih-text-muted ih-truncate'>{k}</span>
              <span className='ih-text-xs ih-font-medium ih-truncate'>
                {typeof v === 'object' && v !== null
                  ? `${(v as { amount?: string }).amount ?? ''} ${
                      (v as { uom?: string }).uom ?? ''
                    }`.trim() || '—'
                  : String(v)}
              </span>
            </div>
          ))}
          {entries.length > 10 && (
            <div className='ih-text-xs ih-text-muted ih-italic'>
              + {entries.length - 10} more…
            </div>
          )}
        </div>
      )}
    </div>
  );
};
